import { create } from "zustand";
import { persist, type PersistStorage } from "zustand/middleware";

/**
 * Layout chrome state — controls the app sidebar (left) and chat history rail.
 *
 * The store carries the user's *pin* preference per rail. The effective
 * render mode (drawer / rail / expanded) is computed elsewhere from
 *   pin × breakpoint × route
 * so the persisted pin is a small, stable surface.
 *
 * "auto" means "follow the route + breakpoint rules":
 *   - On chat surfaces: collapse to rail on tablet/desktop, drawer on phone.
 *   - Elsewhere: expanded on desktop, rail on tablet, drawer on phone.
 *
 * Setting pin to "expanded" or "rail" overrides the auto rule on tablet
 * and desktop — the user's choice wins. (On phones, drawer mode always
 * wins because there isn't room for a permanent rail.)
 */

export type RailPin = "auto" | "expanded" | "rail";

/**
 * Open/closed state for the project-scoped Copilot right rail.
 *
 *   "open"   — rail is visible, conversation surfaces alongside the page
 *   "closed" — rail is collapsed to an edge tab (user re-opens manually)
 *   "auto"   — follow per-route default (closed on canvas-heavy routes
 *              like PCB, open elsewhere)
 *
 * The rail itself decides the effective state from this pin × route.
 * Persisting the pin (rather than the resolved bool) means the user's
 * "I always want this open" gesture survives navigation between routes
 * that have different defaults.
 */
export type CopilotRailPin = "auto" | "open" | "closed";

interface LayoutState {
  /** Pin for the app's primary navigation rail (the left sidebar). */
  appNavPin: RailPin;
  /** Pin for the chat history rail (only renders on chat surfaces). */
  chatHistoryPin: RailPin;
  /** Pin for the project-scoped Copilot right rail. */
  copilotRailPin: CopilotRailPin;
  /**
   * Inline-expand state for the Copilot right rail. When `true` AND
   * the rail is open, the rail widens to share the page 50/50 with
   * the main content (artefact area). When `false`, the rail stays
   * at its compact `COPILOT_RAIL_WIDTH`. Replaces the old "open in
   * full chat" navigation — users now get more room without
   * leaving the workbench. Independent of `copilotRailPin` because
   * the user can pin the rail closed and have a separate preference
   * for the expanded size when they next reopen it.
   */
  copilotRailExpanded: boolean;
  /** Mobile-only — open state of the temporary drawer for the app nav. */
  appNavDrawerOpen: boolean;
  /** Mobile-only — open state of the temporary drawer for chat history. */
  chatHistoryDrawerOpen: boolean;
  /**
   * On chat surfaces, when chat history is in rail mode, this controls
   * the click-peek overlay anchored next to the app nav rail. Distinct
   * from the drawer flag because the peek isn't a portaled modal — it's
   * a positioned overlay rendered inside the chat content area.
   */
  chatHistoryPeekOpen: boolean;

  setAppNavPin: (pin: RailPin) => void;
  setChatHistoryPin: (pin: RailPin) => void;
  setCopilotRailPin: (pin: CopilotRailPin) => void;
  setCopilotRailExpanded: (expanded: boolean) => void;
  toggleCopilotRailExpanded: () => void;
  toggleAppNavDrawer: () => void;
  setAppNavDrawerOpen: (open: boolean) => void;
  toggleChatHistoryDrawer: () => void;
  setChatHistoryDrawerOpen: (open: boolean) => void;
  toggleChatHistoryPeek: () => void;
  setChatHistoryPeekOpen: (open: boolean) => void;
}

// Persist storage with a one-shot migration that imports the four legacy
// flat keys ("cf.layout.appNavPin", etc.) into the new namespaced bag the
// first time the store hydrates. Lets users keep their pin preferences
// across the refactor without UX whiplash.
const LEGACY_KEYS = {
  appNavPin: "cf.layout.appNavPin",
  chatHistoryPin: "cf.layout.chatHistoryPin",
  copilotRailPin: "cf.layout.copilotRailPin",
  copilotRailExpanded: "cf.layout.copilotRailExpanded",
} as const;

function isRailPin(v: unknown): v is RailPin {
  return v === "auto" || v === "expanded" || v === "rail";
}
function isCopilotRailPin(v: unknown): v is CopilotRailPin {
  return v === "auto" || v === "open" || v === "closed";
}

function readLegacyAndPurge(): Partial<{
  appNavPin: RailPin;
  chatHistoryPin: RailPin;
  copilotRailPin: CopilotRailPin;
  copilotRailExpanded: boolean;
}> {
  if (typeof window === "undefined") return {};
  const out: Partial<{
    appNavPin: RailPin;
    chatHistoryPin: RailPin;
    copilotRailPin: CopilotRailPin;
    copilotRailExpanded: boolean;
  }> = {};
  try {
    const a = window.localStorage.getItem(LEGACY_KEYS.appNavPin);
    if (isRailPin(a)) out.appNavPin = a;
    const c = window.localStorage.getItem(LEGACY_KEYS.chatHistoryPin);
    if (isRailPin(c)) out.chatHistoryPin = c;
    const r = window.localStorage.getItem(LEGACY_KEYS.copilotRailPin);
    if (isCopilotRailPin(r)) out.copilotRailPin = r;
    const e = window.localStorage.getItem(LEGACY_KEYS.copilotRailExpanded);
    if (e === "1" || e === "0") out.copilotRailExpanded = e === "1";
    // Purge once we've captured them — the namespaced bag is the new
    // source of truth.
    for (const k of Object.values(LEGACY_KEYS)) {
      window.localStorage.removeItem(k);
    }
  } catch {
    /* private mode etc. — silently fall back to defaults */
  }
  return out;
}

const legacy = readLegacyAndPurge();

type PersistedPart = Pick<
  LayoutState,
  "appNavPin" | "chatHistoryPin" | "copilotRailPin" | "copilotRailExpanded"
>;

// Customise the persist storage so missing keys at first read return the
// imported legacy values once. After that the namespaced bag wins.
const layoutPersistStorage: PersistStorage<PersistedPart> = {
  getItem: (name) => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(name);
      if (raw) return JSON.parse(raw);
    } catch {
      /* fall through */
    }
    if (Object.keys(legacy).length > 0) {
      return { state: legacy as PersistedPart, version: 0 };
    }
    return null;
  },
  setItem: (name, value) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(name, JSON.stringify(value));
    } catch {
      /* ignore — quota, private mode */
    }
  },
  removeItem: (name) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set) => ({
      appNavPin: "auto",
      chatHistoryPin: "auto",
      copilotRailPin: "auto",
      copilotRailExpanded: false,
      appNavDrawerOpen: false,
      chatHistoryDrawerOpen: false,
      chatHistoryPeekOpen: false,

      setAppNavPin: (pin) => set({ appNavPin: pin }),
      setChatHistoryPin: (pin) => set({ chatHistoryPin: pin }),
      setCopilotRailPin: (pin) => set({ copilotRailPin: pin }),
      setCopilotRailExpanded: (expanded) =>
        set({ copilotRailExpanded: expanded }),
      toggleCopilotRailExpanded: () =>
        set((s) => ({ copilotRailExpanded: !s.copilotRailExpanded })),
      toggleAppNavDrawer: () =>
        set((s) => ({ appNavDrawerOpen: !s.appNavDrawerOpen })),
      setAppNavDrawerOpen: (open) => set({ appNavDrawerOpen: open }),
      toggleChatHistoryDrawer: () =>
        set((s) => ({ chatHistoryDrawerOpen: !s.chatHistoryDrawerOpen })),
      setChatHistoryDrawerOpen: (open) => set({ chatHistoryDrawerOpen: open }),
      toggleChatHistoryPeek: () =>
        set((s) => ({ chatHistoryPeekOpen: !s.chatHistoryPeekOpen })),
      setChatHistoryPeekOpen: (open) => set({ chatHistoryPeekOpen: open }),
    }),
    {
      name: "cf.layout",
      storage: layoutPersistStorage,
      // Only the pin/expanded preferences survive a reload. The drawer/peek
      // open flags are ephemeral session state — persisting them would lead
      // to "why is the drawer open after refresh?" surprises.
      partialize: (state) => ({
        appNavPin: state.appNavPin,
        chatHistoryPin: state.chatHistoryPin,
        copilotRailPin: state.copilotRailPin,
        copilotRailExpanded: state.copilotRailExpanded,
      }),
    },
  ),
);
